import { gatewayInstance, posServiceInstance } from './api';

// ==================== MENU ID ====================
export const getMenuIdByBusiness = async (businessId) => {
  const response = await gatewayInstance.get(
    `/api01/menu/find/by/business/${businessId}`
  );
  // API returns { data: ["externalMenuId"], status: "SUCCESS" }
  const externalMenuIds = response.data.data;
  return externalMenuIds && externalMenuIds.length > 0
    ? externalMenuIds[0]
    : null;
};

// ==================== MENU ====================
// `provider: 'main'` matches no price override, so the editor always gets base prices.
// `allowEmptyCategories` keeps categories that have no items yet.
export const getMenuMap = async (externalMenuId, forceFresh = false) => {
  const config = forceFresh
    ? {
        headers: { 'Cache-Control': 'no-cache' },
        params: {
          provider: 'main',
          allowEmptyCategories: true,
          _t: Date.now(), // Cache-busting timestamp
        },
      }
    : { params: { provider: 'main', allowEmptyCategories: true } };

  const response = await gatewayInstance.get(
    `/api01/cloud-pos/menu/map-by-external-id/${externalMenuId}`,
    config
  );
  // API returns { data: { _id, categories, items, ... }, statusCode: 200 }
  // The response contains the menu object with _id as the internal menu ID
  return response.data.data.data || response.data.data || response.data;
};

export const getMenuById = async (menuId) => {
  const response = await gatewayInstance.get(`/api01/cloud-pos/menu/${menuId}`);
  return response.data.data.data || response.data.data || response.data;
};

// ==================== CATEGORIES ====================
export const getCategories = async (menuId) => {
  const response = await gatewayInstance.get(`/api01/cloud-pos/menu/category`, {
    params: { menuId },
  });
  return response.data.data.data || response.data.data || response.data;
};

export const getCategoryById = async (categoryId) => {
  const response = await gatewayInstance.get(
    `/api01/cloud-pos/menu/category/${categoryId}`
  );
  return response.data.data.data || response.data.data || response.data;
};

export const createCategory = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/category',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const updateCategory = async (categoryId, data) => {
  const response = await gatewayInstance.patch(
    `/api01/cloud-pos/menu/category/${categoryId}`,
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const deleteCategory = async (categoryId) => {
  const response = await gatewayInstance.delete(
    `/api01/cloud-pos/menu/category/${categoryId}`
  );
  return response.data.data.data || response.data.data || response.data;
};

// ==================== ITEMS ====================
export const getItemById = async (itemId) => {
  const response = await gatewayInstance.get(
    `/api01/cloud-pos/menu/item/${itemId}`
  );
  return response.data.data.data || response.data.data || response.data;
};

export const createItem = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/item',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const updateItem = async (itemId, data) => {
  const response = await gatewayInstance.patch(
    `/api01/cloud-pos/menu/item/${itemId}`,
    data
  );
  // Gateway wraps the Cloud POS API response:
  // response.data.data.data contains the actual item
  return response.data?.data?.data || response.data?.data || response.data;
};

export const deleteItem = async (itemId) => {
  const response = await gatewayInstance.delete(
    `/api01/cloud-pos/menu/item/${itemId}`
  );
  return response.data.data.data || response.data.data || response.data;
};

export const getItemPriceOverrides = async (itemId, provider) => {
  const response = await gatewayInstance.get(
    `/api01/cloud-pos/menu/item/${itemId}/price-overrides`,
    { params: { provider } }
  );
  return response.data?.data?.data || response.data?.data || response.data;
};

// ==================== PROVIDERS SETTINGS ====================
export const getPosProvidersSettings = async (externalMenuId) => {
  const response = await gatewayInstance.get(
    `/api01/cloud-pos/menu/providers-settings/by-external-id/${externalMenuId}`
  );
  return response.data?.data?.data || response.data?.data || response.data;
};

export const updatePosProvidersSettings = async (externalMenuId, data) => {
  const response = await gatewayInstance.patch(
    `/api01/cloud-pos/menu/providers-settings/by-external-id/${externalMenuId}`,
    data
  );
  return response.data?.data?.data || response.data?.data || response.data;
};

// Creates the initial inventory record for a POS menu item.
export const generateItemInventory = async (itemId, payload = {}) => {
  const response = await gatewayInstance.post(
    `/api01/cloud-pos/menu/item/${itemId}/inventory`,
    payload
  );
  return response.data?.data?.data || response.data?.data || response.data;
};

export const updateItemSoldOut = async (itemId, soldOutData) => {
  const response = await gatewayInstance.put(
    `/api01/cloud-pos/menu/item/soldOut/${itemId}`,
    soldOutData
  );
  return response.data.data.data || response.data.data || response.data;
};

// ==================== MODIFIER GROUPS ====================
export const getModifierGroupById = async (modifierGroupId) => {
  const response = await gatewayInstance.get(
    `/api01/cloud-pos/menu/modifier-group/${modifierGroupId}`
  );
  return response.data.data.data || response.data.data || response.data;
};

export const createModifierGroup = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/modifier-group',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const updateModifierGroup = async (modifierGroupId, data) => {
  const response = await gatewayInstance.patch(
    `/api01/cloud-pos/menu/modifier-group/${modifierGroupId}`,
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const deleteModifierGroup = async (modifierGroupId) => {
  const response = await gatewayInstance.delete(
    `/api01/cloud-pos/menu/modifier-group/${modifierGroupId}`
  );
  return response.data.data.data || response.data.data || response.data;
};

// ==================== INGREDIENTS ====================
export const getIngredients = async (menuId) => {
  const response = await gatewayInstance.get(
    `/api01/cloud-pos/menu/ingredient`,
    {
      params: { menuId },
    }
  );
  return response.data.data.data || response.data.data || response.data;
};

export const createIngredient = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/ingredient',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const updateIngredient = async (ingredientId, data) => {
  const response = await gatewayInstance.put(
    `/api01/cloud-pos/menu/ingredient/${ingredientId}`,
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const deleteIngredient = async (ingredientId) => {
  const response = await gatewayInstance.delete(
    `/api01/cloud-pos/menu/ingredient/${ingredientId}`
  );
  return response.data.data.data || response.data.data || response.data;
};

// ==================== RELATIONSHIPS ====================

// Category-Item Relationships
export const createCategoryItem = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/category-item',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const deleteCategoryItem = async (data) => {
  const response = await gatewayInstance.delete(
    '/api01/cloud-pos/menu/category-item',
    { data }
  );
  return response.data.data.data || response.data.data || response.data;
};

// Item-Modifier Group Relationships
export const createItemModifierGroup = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/item-modifier-group',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const deleteItemModifierGroup = async (data) => {
  const response = await gatewayInstance.delete(
    '/api01/cloud-pos/menu/item-modifier-group',
    {
      data, // Use query params instead of request body
    }
  );
  return response.data.data.data || response.data.data || response.data;
};

// Item-Ingredient Relationships
export const createItemIngredient = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/item-ingredient',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const deleteItemIngredient = async (data) => {
  const response = await gatewayInstance.delete(
    '/api01/cloud-pos/menu/item-ingredient',
    { data }
  );
  return response.data.data.data || response.data.data || response.data;
};

// Modifier Group Options (items that are options in a modifier group)
export const createModifierGroupOption = async (data) => {
  const response = await gatewayInstance.post(
    '/api01/cloud-pos/menu/modifier-group-option',
    data
  );
  return response.data.data.data || response.data.data || response.data;
};

export const deleteModifierGroupOption = async (data) => {
  const response = await gatewayInstance.delete(
    '/api01/cloud-pos/menu/modifier-group-option',
    {
      data: data, // Send in request body
    }
  );
  return response.data.data.data || response.data.data || response.data;
};

// ==================== MENU IMPORT ====================
export const importMenuMap = async (businessUuid, menu) => {
  const response = await posServiceInstance.post('/menu-import/map', {
    businessUuid,
    menu,
  });
  return response.data;
};

export const emptyMenu = async (businessUuid) => {
  const response = await posServiceInstance.post(
    `/menu-import/business/${businessUuid}/empty`,
    { businessUuid }
  );
  return response.data;
};

// ==================== HELPER FUNCTIONS ====================
export const buildTranslationObject = (enValue, esValue = '') => {
  return {
    en: enValue,
    ...(esValue && { es: esValue }),
  };
};

export const getTranslationValue = (translationObj, lang = 'en') => {
  if (!translationObj) return '';
  return translationObj[lang] || translationObj.en || '';
};
